/** Links to the legal pages + the mandatory consent checkbox (registration, guest upgrade). */
import { LEGAL_PATHS, LEGAL_TITLES, LEGAL_VERSIONS, MIN_AGE, type LegalDocKey } from '@galaxis/shared';

/** Footer row: the four documents. Opens in the same tab (the pages have a way back). */
export function LegalFooter({ keys = ['privacy', 'terms', 'cookies', 'imprint'], className = '' }: { keys?: LegalDocKey[]; className?: string }) {
  return (
    <nav aria-label="Jogi tájékoztatók" className={`flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-slate-500 ${className}`} data-testid="legal-footer">
      {keys.map(k => (
        <a key={k} href={LEGAL_PATHS[k]} className="underline underline-offset-2 hover:text-slate-300 inline-flex items-center" style={{ minHeight: 44 }} data-testid={`legal-link-${k}`}>
          {LEGAL_TITLES[k]}
        </a>
      ))}
    </nav>
  );
}

/** What the page shows in the consent: the registration sends it back so a stale page cannot consent to unseen text. */
export const CONSENT_VERSIONS = { terms: LEGAL_VERSIONS.terms, privacy: LEGAL_VERSIONS.privacy };

/** Not pre-ticked. The links open in a new tab so the half-filled form survives. */
export function LegalConsent({ checked, onChange, error, id = 'legal-consent' }: { checked: boolean; onChange: (v: boolean) => void; error?: string; id?: string }) {
  return (
    <div>
      <label htmlFor={id} className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer" style={{ minHeight: 44 }}>
        <input
          id={id} type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          className="mt-1 flex-shrink-0" style={{ width: 20, height: 20 }} data-testid="legal-consent"
          aria-invalid={error ? 'true' : undefined} aria-describedby={error ? `${id}-err` : undefined}
        />
        <span>
          Elmúltam {MIN_AGE} éves, és elfogadom a{' '}
          <a href={LEGAL_PATHS.terms} target="_blank" rel="noopener" className="text-accent underline underline-offset-2">Felhasználási feltételeket</a>
          {' '}és az{' '}
          <a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener" className="text-accent underline underline-offset-2">Adatkezelési tájékoztatót</a>.
        </span>
      </label>
      {error && <p id={`${id}-err`} className="field-error" role="alert">{error}</p>}
    </div>
  );
}

/** Short information line for guest play (no account, no e-mail: nothing to accept, but the notice is one click away). */
export function GuestPrivacyLine() {
  return (
    <p className="text-xs text-slate-500 text-center mt-2" data-testid="guest-privacy-line">
      Vendégként is a <a href={LEGAL_PATHS.terms} target="_blank" rel="noopener" className="underline underline-offset-2">Felhasználási feltételek</a> szerint játszol; az adataidat az{' '}
      <a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener" className="underline underline-offset-2">Adatkezelési tájékoztató</a> szerint kezeljük. A játék {MIN_AGE} éven felülieknek szól.
    </p>
  );
}
